import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/feature/Toast';
import { useAuth } from '@/context/AuthContext';
import DeleteConfirmModal from '@/components/feature/DeleteConfirmModal';
import { NOMBRE_FARMACIA, SISTEMA_DESCRIPCION, SISTEMA_NOMBRE } from '@/lib/marca';

export default function ConfiguracionPage() {
  const { addToast } = useToast();
  const { user } = useAuth();

  const [showClearVentas, setShowClearVentas] = useState(false);
  const [showClearHistorial, setShowClearHistorial] = useState(false);
  const [clearingVentas, setClearingVentas] = useState(false);
  const [clearingHistorial, setClearingHistorial] = useState(false);

  const handleClearVentas = async () => {
    setClearingVentas(true);
    try {
      await supabase.from('detalle_venta').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      await supabase.from('ventas').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      await supabase.from('auditoria').insert({
        usuario: user?.nombre || 'Sistema',
        accion: 'Eliminó todo el historial de ventas',
        modulo: 'Configuración',
      });
      addToast('Historial de ventas eliminado correctamente', 'success');
    } catch (err: unknown) {
      addToast(err instanceof Error ? err.message : 'Error al limpiar ventas', 'error');
    } finally {
      setClearingVentas(false);
      setShowClearVentas(false);
    }
  };

  const handleClearHistorial = async () => {
    setClearingHistorial(true);
    try {
      await supabase.from('auditoria').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      await supabase.from('kardex').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      await supabase.from('auditoria').insert({
        usuario: user?.nombre || 'Sistema',
        accion: 'Eliminó todo el historial de productos (auditoría y kardex)',
        modulo: 'Configuración',
      });
      addToast('Historial de productos eliminado correctamente', 'success');
    } catch (err: unknown) {
      addToast(err instanceof Error ? err.message : 'Error al limpiar historial de productos', 'error');
    } finally {
      setClearingHistorial(false);
      setShowClearHistorial(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold text-foreground-800">Configuración</h1>
        <p className="text-foreground-500 text-sm mt-1">Ajustes del sistema y gestión de datos</p>
      </div>

      <div className="bg-white rounded-xl border border-background-200 p-5">
        <h2 className="font-heading text-base font-semibold text-foreground-800 mb-1">Información del Sistema</h2>
        <p className="text-sm text-foreground-500 mb-4">Resumen general del estado actual de la base de datos</p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <InfoCard icon="ri-database-2-line" iconBg="bg-primary-100" iconColor="text-primary-600" label="Base de Datos" value="PostgreSQL (Supabase)" />
          <InfoCard icon="ri-server-line" iconBg="bg-accent-100" iconColor="text-accent-600" label="Backend" value="SaaS Supabase" />
          <InfoCard icon="ri-shield-check-line" iconBg="bg-secondary-100" iconColor="text-secondary-600" label="Sesión" value={user?.nombre || 'Activa'} sub={user?.rol === 'super_admin' ? 'Super Admin' : 'Usuario'} />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-accent-200 p-5">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 bg-accent-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <i className="ri-alert-line text-xl text-accent-600"></i>
          </div>
          <div>
            <h2 className="font-heading text-base font-semibold text-foreground-800">Limpieza de Datos Históricos</h2>
            <p className="text-sm text-foreground-500 mt-1">
              Estas acciones eliminan permanentemente los datos históricos. <strong className="text-accent-600 font-semibold">Esta acción es irreversible.</strong>
            </p>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 rounded-lg bg-background-50 border border-background-200">
            <div>
              <p className="text-sm font-medium text-foreground-700">Limpiar Historial de Ventas</p>
              <p className="text-xs text-foreground-400">Elimina todas las ventas y sus detalles</p>
            </div>
            <button
              onClick={() => setShowClearVentas(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-accent-500 hover:bg-accent-600 text-white text-sm font-medium rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-delete-bin-line"></i>
              <span>Limpiar Ventas</span>
            </button>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 rounded-lg bg-background-50 border border-background-200">
            <div>
              <p className="text-sm font-medium text-foreground-700">Limpiar Historial de Productos</p>
              <p className="text-xs text-foreground-400">Elimina todos los registros de auditoría y movimientos de inventario de productos</p>
            </div>
            <button
              onClick={() => setShowClearHistorial(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-accent-500 hover:bg-accent-600 text-white text-sm font-medium rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-delete-bin-line"></i>
              <span>Limpiar Historial</span>
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-background-200 p-5">
        <h2 className="font-heading text-base font-semibold text-foreground-800 mb-1">Acerca de</h2>
        <p className="text-sm text-foreground-500 mb-3">{NOMBRE_FARMACIA} — {SISTEMA_DESCRIPCION}</p>
        <div className="text-sm text-foreground-600 space-y-1">
          <p>Sistema: {SISTEMA_NOMBRE}</p>
          <p>Versión 2.0</p>
          <p>Stack: React 19 + TypeScript + TailwindCSS + Supabase</p>
          <p>Módulos: Dashboard, Ventas, Productos, Reportes, Usuarios, Configuración</p>
          <p className="text-xs text-foreground-400 mt-3">&copy; {new Date().getFullYear()} {NOMBRE_FARMACIA}. Todos los derechos reservados.</p>
        </div>
      </div>

      <DeleteConfirmModal
        open={showClearVentas}
        onClose={() => setShowClearVentas(false)}
        onConfirm={handleClearVentas}
        loading={clearingVentas}
        title="Limpiar Historial de Ventas"
        message="¿Estás seguro? Se eliminarán TODAS las ventas y sus detalles. Esta acción es irreversible y NO se puede deshacer."
      />

      <DeleteConfirmModal
        open={showClearHistorial}
        onClose={() => setShowClearHistorial(false)}
        onConfirm={handleClearHistorial}
        loading={clearingHistorial}
        title="Limpiar Historial de Productos"
        message="¿Estás seguro? Se eliminarán TODOS los registros de auditoría y los movimientos de inventario (kardex). Esta acción es irreversible y NO se puede deshacer."
      />
    </div>
  );
}

function InfoCard({ icon, iconBg, iconColor, label, value, sub }: {
  icon: string; iconBg: string; iconColor: string; label: string; value: string; sub?: string;
}) {
  return (
    <div className="flex items-center gap-3 p-4 rounded-lg bg-background-50 border border-background-200">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${iconBg} ${iconColor}`}>
        <i className={icon}></i>
      </div>
      <div>
        <p className="text-xs text-foreground-500">{label}</p>
        <p className="text-sm font-semibold text-foreground-800">{value}</p>
        {sub && <p className="text-xs text-foreground-400">{sub}</p>}
      </div>
    </div>
  );
}